'use client';

import CopyIconSvg from '@/assets/svgs/copy-outline.svg';
import ContactListsIconSvg from '@/assets/svgs/main/contact-lists.svg';
import SplitIconSvg from '@/assets/svgs/split.svg';
import TrashCanIconSvg from '@/assets/svgs/trash-can.svg';
import { useFetchAccountDetails as useFetchAccountDetailsFromAccounts } from '@/containers/Accounts/api';
import { useFetchContactListDetails } from '@/containers/ContactLists/api';
import { CONTACT_LIST_TYPE_MAP } from '@/containers/ContactLists/types';
import { Modals } from '@/containers/Modals/all-modals';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import {
  selectAccountIdentities,
  selectCurrentAccountId,
} from '@/lib/redux/app.selectors';
import { BulkActionButton, ModalActionsContainer } from '@/shared/components/BulkActions';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { ContactListDetailsModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import { ContactListDetailsSection } from './ContactListDetailsSection';
import { ContactsCard } from './ContactsCard';
import { OptOutsCard } from './OptOutsCard';
import { SpotlightCard } from './SpotlightCard';
import { SublistsCard } from './SublistsCard';

const modalName = Modals.ContactListDetailsModal;

interface ContactListDetailsModalProps extends ModalProps {
  className?: string;
  params?: ContactListDetailsModalParams;
}

const ContactListDetailsModal = ({
  className,
  params,
  variant = 'narrow',
}: ContactListDetailsModalProps) => {
  const { isModalOpen, onClose } = useModals({
    modalName,
    shouldCloseOnEscape: true,
  });
  const identities = useSelector(selectAccountIdentities);
  const currentAccountId = useSelector(selectCurrentAccountId);
  const { data: accountDetails } = useFetchAccountDetailsFromAccounts(
    currentAccountId || undefined,
    !!currentAccountId
  );
  const hasRateplan = !!accountDetails?.data?.p2p_rateplan;
  const { showError } = useToast();

  // State for sublist selections
  const [selectedSublists, setSelectedSublists] = useState<Set<number>>(new Set());
  const [selectAllSublists, setSelectAllSublists] = useState(false);

  // Action handlers (stubbed for now)
  const onSplit = useCallback(() => {
    console.log('Split action triggered');
    // TODO: Implement split functionality
  }, []);

  const onClone = useCallback(() => {
    console.log('Clone action triggered');
    // TODO: Implement clone functionality
  }, []);

  const onDelete = useCallback(() => {
    console.log('Delete action triggered');
    // TODO: Implement delete functionality
  }, []);

  // Modal specific details
  const { listId } = params || {};

  // Fetch contact list details
  const {
    data: contactListDetailsResponse,
    isLoading,
    error,
  } = useFetchContactListDetails(listId, isModalOpen);

  const contactListDetails = contactListDetailsResponse?.data;

  // Close modal and show error if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage =
        (error as { response?: { data?: { Error?: string } } })?.response?.data?.Error ||
        'Failed to load contact list details';
      showError(errorMessage);
      onClose();
    }
  }, [error, showError, onClose]);

  // Clear sublist selections when modal closes or contact list changes
  useEffect(() => {
    if (!isModalOpen) {
      setSelectedSublists(new Set());
      setSelectAllSublists(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    setSelectedSublists(new Set());
    setSelectAllSublists(false);
  }, [contactListDetails?.list_id]);

  // Get the identity name from the identities array
  const identityName = useMemo(() => {
    if (!contactListDetails?.identity_id || !identities.length)
      return contactListDetails?.identity_id || '-';
    const identity = identities.find(
      (id) => id.identity_id === contactListDetails.identity_id
    );
    return identity?.identity_name || contactListDetails.identity_id;
  }, [contactListDetails?.identity_id, identities]);

  // Get the contact list type
  const listType = useMemo(() => {
    if (!contactListDetails?.suppress_cell_phones) return null;
    return CONTACT_LIST_TYPE_MAP[contactListDetails.suppress_cell_phones] || null;
  }, [contactListDetails?.suppress_cell_phones]);

  // Sublist selection handlers
  const handleSelectSublist = useCallback(
    (sublistId: number, selected: boolean) => {
      setSelectedSublists((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(sublistId);
        } else {
          newSet.delete(sublistId);
        }

        // Update select all state
        const totalSublists = contactListDetails?.Sublists?.length || 0;
        setSelectAllSublists(newSet.size === totalSublists && totalSublists > 0);

        return newSet;
      });
    },
    [contactListDetails?.Sublists?.length]
  );

  const handleSelectAllSublists = useCallback(
    (selected: boolean) => {
      setSelectAllSublists(selected);
      if (selected && contactListDetails?.Sublists) {
        setSelectedSublists(
          new Set(contactListDetails.Sublists.map((s) => s.sublist_id))
        );
      } else {
        setSelectedSublists(new Set());
      }
    },
    [contactListDetails?.Sublists]
  );

  // Determine if select all checkbox should be indeterminate
  const isSelectAllIndeterminate = useMemo(() => {
    const selectedCount = selectedSublists.size;
    const totalCount = contactListDetails?.Sublists?.length || 0;
    return selectedCount > 0 && selectedCount < totalCount;
  }, [selectedSublists.size, contactListDetails?.Sublists?.length]);

  if (!isModalOpen) return null;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}
        shouldCloseOnBackgroundClick>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="url"
            icon={<ContactListsIconSvg className="h-5 w-5" />}
            title={
              isLoading
                ? 'Loading...'
                : contactListDetails?.list_name || 'Contact List Details'
            }
          />

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}

          {/* Content */}
          {!isLoading && contactListDetails && (
            <div className="flex flex-1 overflow-hidden">
              {/* Left Panel - Details */}
              <div className="flex flex-1 flex-col overflow-y-auto p-6 pb-24">
                {/* Top Details Section */}
                <ContactListDetailsSection
                  listState={contactListDetails.list_state}
                  uploadBy={contactListDetails.upload_by}
                  uploadDate={contactListDetails.upload_date}
                  listType={listType}
                  identityName={identityName}
                  leadsLoaded={contactListDetails.leads_loaded}
                  leadsRemaining={contactListDetails.leads_remaining}
                />

                {/* Contact Stats Card */}
                <ContactsCard
                  leadsRemaining={contactListDetails.leads_remaining}
                  leadsLoaded={contactListDetails.leads_loaded}
                  leadsSupplied={contactListDetails.leads_supplied}
                  leadsInvalid={contactListDetails.leads_invalid}
                  leadsDuplicate={contactListDetails.leads_duplicate}
                  leadsCellSuppressed={contactListDetails.leads_cell_suppressed}
                />

                {/* Spotlight Card */}
                {hasRateplan &&
                  contactListDetails.uses_rateplan === 1 &&
                  contactListDetails.rateplan_adjusted &&
                  contactListDetails.rateplan_adjusted > 0 && (
                    <SpotlightCard
                      rateplanSavingsPerc={contactListDetails.rateplan_savings_perc}
                      rateplanSavingsTotalCountPerc={
                        contactListDetails.rateplan_savings_total_count_perc
                      }
                      rateplanBreakdown={contactListDetails.rateplan_breakdown}
                      p2pRateplan={accountDetails?.data?.p2p_rateplan}
                    />
                  )}

                {/* Opt Outs Card */}
                <OptOutsCard
                  leadsNatDnc={contactListDetails.leads_nat_dnc}
                  leadsStateDnc={contactListDetails.leads_state_dnc}
                  leadsCellDnc={contactListDetails.leads_cell_dnc}
                  leadsMasterDnc={contactListDetails.leads_master_dnc}
                  leadsAcctDnc={contactListDetails.leads_acct_dnc}
                  leadsInvalid={contactListDetails.leads_invalid}
                />

                {/* Sublists Card */}
                <SublistsCard
                  sublists={contactListDetails.Sublists || []}
                  selectedSublists={selectedSublists}
                  selectAllSublists={selectAllSublists}
                  isSelectAllIndeterminate={isSelectAllIndeterminate}
                  onSelectSublist={handleSelectSublist}
                  onSelectAllSublists={handleSelectAllSublists}
                  leadsLoaded={contactListDetails.leads_loaded}
                  leadsRemaining={contactListDetails.leads_remaining}
                />
              </div>
            </div>
          )}

          {/* Modal Actions Container */}
          {!isLoading && contactListDetails && (
            <ModalActionsContainer>
              <BulkActionButton
                icon={<SplitIconSvg className="h-4 w-4" />}
                label="Split"
                onClick={onSplit}
              />
              <BulkActionButton
                icon={<CopyIconSvg className="h-4 w-4" />}
                label="Clone"
                onClick={onClone}
              />
              <BulkActionButton
                icon={<TrashCanIconSvg className="h-4 w-4" />}
                label="Delete"
                onClick={onDelete}
                className="text-red-600 hover:bg-red-50"
              />
            </ModalActionsContainer>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default ContactListDetailsModal;
